import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trophy, Trash2, Users, GitBranch, Crown, ChevronRight, Edit2, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MatchCard = ({ match, teams, onSetWinner, isChampionship }) => {
  const t1 = teams.find(t => t.id === match.team1_id);
  const t2 = teams.find(t => t.id === match.team2_id);
  const [editing, setEditing] = useState(false);
  const [s1, setS1] = useState(match.team1_score ?? '');
  const [s2, setS2] = useState(match.team2_score ?? '');

  const saveScore = () => {
    const score1 = parseInt(s1) || 0;
    const score2 = parseInt(s2) || 0;
    const winnerId = score1 > score2 ? match.team1_id : score2 > score1 ? match.team2_id : null;
    onSetWinner(match.id, { team1_score: score1, team2_score: score2, winner_id: winnerId });
    setEditing(false);
  };

  return (
    <div className={cn(
      "bg-stone-800 rounded-lg border-2 overflow-hidden w-56 shrink-0",
      isChampionship ? "border-amber-500 shadow-lg shadow-amber-500/20" : "border-stone-600",
      match.winner_id ? "opacity-100" : "opacity-90"
    )} data-testid={`match-${match.id}`}>
      {isChampionship && (
        <div className="bg-amber-600 text-center py-0.5 text-[10px] text-white font-bold tracking-wider">
          CHAMPIONSHIP
        </div>
      )}
      {/* Team 1 */}
      <div className={cn(
        "flex items-center justify-between px-3 py-2 border-b border-stone-700 cursor-pointer transition-colors",
        match.winner_id === match.team1_id ? "bg-green-900/40" : "hover:bg-stone-700"
      )}
        onClick={() => !editing && match.team1_id && match.team2_id && onSetWinner(match.id, { winner_id: match.team1_id })}
      >
        <span className={cn("text-sm truncate flex-1", t1 ? "text-white" : "text-stone-600 italic")}>
          {t1?.name || (match.team1_id ? 'TBD' : '---')}
        </span>
        {editing ? (
          <input type="number" value={s1} onChange={e => setS1(e.target.value)}
            className="w-10 bg-stone-700 border border-stone-500 rounded text-center text-white text-sm" />
        ) : (
          match.team1_score !== null && <span className="text-white font-bold text-sm ml-2">{match.team1_score}</span>
        )}
        {match.winner_id === match.team1_id && <Crown size={12} className="text-amber-400 ml-1 shrink-0" />}
      </div>
      {/* Team 2 */}
      <div className={cn(
        "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
        match.winner_id === match.team2_id ? "bg-green-900/40" : "hover:bg-stone-700"
      )}
        onClick={() => !editing && match.team1_id && match.team2_id && onSetWinner(match.id, { winner_id: match.team2_id })}
      >
        <span className={cn("text-sm truncate flex-1", t2 ? "text-white" : "text-stone-600 italic")}>
          {t2?.name || (match.team2_id ? 'TBD' : '---')}
        </span>
        {editing ? (
          <input type="number" value={s2} onChange={e => setS2(e.target.value)}
            className="w-10 bg-stone-700 border border-stone-500 rounded text-center text-white text-sm" />
        ) : (
          match.team2_score !== null && <span className="text-white font-bold text-sm ml-2">{match.team2_score}</span>
        )}
        {match.winner_id === match.team2_id && <Crown size={12} className="text-amber-400 ml-1 shrink-0" />}
      </div>
      {/* Score edit button */}
      {match.team1_id && match.team2_id && (
        <div className="border-t border-stone-700 px-2 py-1 flex justify-end">
          {editing ? (
            <button onClick={saveScore} className="text-green-400 text-[10px] flex items-center gap-0.5 hover:text-green-300">
              <Check size={10} /> Save
            </button>
          ) : (
            <button onClick={() => setEditing(true)} className="text-stone-500 text-[10px] flex items-center gap-0.5 hover:text-stone-300">
              <Edit2 size={10} /> Score
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function TournamentPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [tournaments, setTournaments] = useState([]);
  const [activeTournament, setActiveTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  // Create form
  const [tName, setTName] = useState('');
  const [tFormat, setTFormat] = useState('single_elimination');
  const [tNumTeams, setTNumTeams] = useState(8);
  const [tPools, setTPools] = useState(['Pool A', 'Pool B']);
  const [tTeamNames, setTTeamNames] = useState([]);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadTournaments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tournaments`, { headers });
      if (res.ok) setTournaments(await res.json());
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const handleCreate = async () => {
    if (!tName.trim()) { toast.error('Enter a tournament name'); return; }
    const teams = tTeamNames.filter(n => n.trim()).map((name, i) => ({
      id: `t${i + 1}`,
      name: name.trim(),
      seed: i + 1,
      pool: tFormat === 'pools_then_bracket' ? tPools[i % tPools.length] : null
    }));
    try {
      const res = await fetch(`${API}/api/tournaments`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: tName, format: tFormat, num_teams: tNumTeams, pools: tFormat === 'pools_then_bracket' ? tPools : [], teams })
      });
      if (res.ok) {
        const t = await res.json();
        setTournaments(prev => [...prev, t]);
        setActiveTournament(t);
        setCreateOpen(false);
        toast.success('Tournament created!');
      }
    } catch { toast.error('Failed to create tournament'); }
  };

  const handleSetWinner = async (matchId, updates) => {
    if (!activeTournament) return;
    const newBracket = activeTournament.bracket.map(m => {
      if (m.id === matchId) return { ...m, ...updates };
      return m;
    });
    // Advance winner to next round
    const match = newBracket.find(m => m.id === matchId);
    if (match?.winner_id) {
      const nextRound = match.round_num + 1;
      const nextMatchNum = Math.ceil(match.match_num / 2);
      const nextMatch = newBracket.find(m => m.round_num === nextRound && m.match_num === nextMatchNum);
      if (nextMatch) {
        if (match.match_num % 2 === 1) nextMatch.team1_id = match.winner_id;
        else nextMatch.team2_id = match.winner_id;
      }
      // Check if this is the championship and set champion
      const maxRound = Math.max(...newBracket.map(m => m.round_num));
      if (match.round_num === maxRound) {
        activeTournament.champion_id = match.winner_id;
      }
    }
    const updated = { ...activeTournament, bracket: newBracket };
    setActiveTournament(updated);
    setTournaments(prev => prev.map(t => t.id === updated.id ? updated : t));
    try {
      await fetch(`${API}/api/tournaments/${activeTournament.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ bracket: newBracket, champion_id: updated.champion_id })
      });
    } catch {}
  };

  const handlePoolScore = async (gameId, updates) => {
    if (!activeTournament) return;
    const newPoolGames = activeTournament.pool_games.map(g => {
      if (g.id === gameId) return { ...g, ...updates };
      return g;
    });
    const updated = { ...activeTournament, pool_games: newPoolGames };
    setActiveTournament(updated);
    try {
      await fetch(`${API}/api/tournaments/${activeTournament.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ pool_games: newPoolGames })
      });
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/api/tournaments/${id}`, { method: 'DELETE', headers });
      setTournaments(prev => prev.filter(t => t.id !== id));
      if (activeTournament?.id === id) setActiveTournament(null);
      toast.success('Deleted');
    } catch {}
  };

  const maxRound = activeTournament?.bracket?.length ? Math.max(...activeTournament.bracket.map(m => m.round_num)) : 0;
  const champion = activeTournament?.champion_id ? activeTournament.teams.find(t => t.id === activeTournament.champion_id) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 p-4 md:p-6" data-testid="tournament-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={16} />
            </Button>
            <div>
              <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                TOURNAMENT BRACKETS
              </h1>
              <p className="text-stone-500 text-xs">Build brackets, track pools, crown champions</p>
            </div>
          </div>
          <Button className="bg-amber-600 hover:bg-amber-500 text-white" onClick={() => {
            setTName(''); setTFormat('single_elimination'); setTNumTeams(8); setTPools(['Pool A', 'Pool B']);
            setTTeamNames(Array(8).fill('')); setCreateOpen(true);
          }} data-testid="create-tournament-btn">
            <Plus size={16} className="mr-1" /> New Tournament
          </Button>
        </div>

        {/* Tournament List */}
        {!activeTournament && (
          <div className="space-y-3">
            {tournaments.length === 0 && (
              <Card className="border-0 bg-stone-900">
                <CardContent className="py-12 text-center">
                  <GitBranch size={40} className="mx-auto text-stone-600 mb-3" />
                  <p className="text-stone-400">No tournaments yet</p>
                  <p className="text-stone-600 text-sm mt-1">Create your first tournament bracket!</p>
                </CardContent>
              </Card>
            )}
            {tournaments.map(t => (
              <Card key={t.id} className="border-0 bg-stone-900 hover:bg-stone-800/80 cursor-pointer transition-colors" onClick={() => setActiveTournament(t)}>
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-700/30 flex items-center justify-center">
                      <Trophy size={18} className="text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold" data-testid={`tournament-name-${t.id}`}>{t.name}</h3>
                      <p className="text-stone-500 text-xs">{t.num_teams} teams - {t.format === 'pools_then_bracket' ? 'Pools + Bracket' : 'Single Elimination'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.champion_id && <span className="text-amber-400 text-xs font-bold">Champion Crowned</span>}
                    <ChevronRight size={16} className="text-stone-600" />
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDelete(t.id); }} data-testid={`delete-tournament-${t.id}`}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Active Tournament View */}
        {activeTournament && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="sm" className="text-stone-400" onClick={() => setActiveTournament(null)}>
                <ArrowLeft size={14} className="mr-1" /> All Tournaments
              </Button>
              <h2 className="text-white text-xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {activeTournament.name}
              </h2>
            </div>

            {/* Champion Banner */}
            {champion && (
              <Card className="border-2 border-amber-500 bg-gradient-to-r from-amber-900/40 to-stone-900" data-testid="champion-banner">
                <CardContent className="py-4 text-center">
                  <Crown size={28} className="mx-auto text-amber-400 mb-2" />
                  <p className="text-amber-400 text-xs font-bold tracking-wider">CHAMPION</p>
                  <p className="text-white text-2xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{champion.name}</p>
                </CardContent>
              </Card>
            )}

            {/* Pool Play Section */}
            {activeTournament.format === 'pools_then_bracket' && activeTournament.pool_games?.length > 0 && (
              <Card className="border-0 bg-stone-900">
                <CardHeader>
                  <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <Users size={14} className="inline mr-2 text-blue-400" />POOL PLAY
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activeTournament.pools.map(poolName => (
                    <div key={poolName} className="mb-4">
                      <h4 className="text-blue-400 text-xs font-bold mb-2">{poolName}</h4>
                      <div className="space-y-1">
                        {activeTournament.pool_games.filter(g => g.pool === poolName).map(game => {
                          const t1 = activeTournament.teams.find(t => t.id === game.team1_id);
                          const t2 = activeTournament.teams.find(t => t.id === game.team2_id);
                          return (
                            <div key={game.id} className="flex items-center gap-2 bg-stone-800 rounded px-3 py-2 text-sm">
                              <span className={cn("flex-1 text-right", game.winner_id === game.team1_id ? "text-green-400 font-bold" : "text-white")}>
                                {t1?.name || 'TBD'}
                              </span>
                              <div className="flex items-center gap-1 px-2">
                                <input type="number" value={game.team1_score ?? ''} placeholder="-" className="w-8 bg-stone-700 border border-stone-600 rounded text-center text-white text-xs h-6"
                                  onChange={e => {
                                    const s1 = parseInt(e.target.value) || 0;
                                    const s2 = game.team2_score || 0;
                                    handlePoolScore(game.id, { team1_score: s1, winner_id: s1 > s2 ? game.team1_id : s2 > s1 ? game.team2_id : null });
                                  }} />
                                <span className="text-stone-600 text-xs">vs</span>
                                <input type="number" value={game.team2_score ?? ''} placeholder="-" className="w-8 bg-stone-700 border border-stone-600 rounded text-center text-white text-xs h-6"
                                  onChange={e => {
                                    const s2 = parseInt(e.target.value) || 0;
                                    const s1 = game.team1_score || 0;
                                    handlePoolScore(game.id, { team2_score: s2, winner_id: s1 > s2 ? game.team1_id : s2 > s1 ? game.team2_id : null });
                                  }} />
                              </div>
                              <span className={cn("flex-1", game.winner_id === game.team2_id ? "text-green-400 font-bold" : "text-white")}>
                                {t2?.name || 'TBD'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Bracket */}
            <Card className="border-0 bg-stone-900">
              <CardHeader>
                <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <GitBranch size={14} className="inline mr-2 text-amber-400" />BRACKET
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto pb-4">
                  <div className="flex gap-8 min-w-max items-start">
                    {Array.from({ length: maxRound }, (_, r) => r + 1).map(roundNum => {
                      const roundMatches = activeTournament.bracket.filter(m => m.round_num === roundNum);
                      const roundLabel = roundNum === maxRound ? 'Finals' : roundNum === maxRound - 1 ? 'Semifinals' : `Round ${roundNum}`;
                      return (
                        <div key={roundNum} className="flex flex-col items-center">
                          <p className="text-stone-500 text-xs font-bold mb-3 uppercase tracking-wider">{roundLabel}</p>
                          <div className="flex flex-col gap-4 justify-around" style={{ minHeight: roundNum > 1 ? `${roundMatches.length * 120}px` : 'auto' }}>
                            {roundMatches.map(match => (
                              <MatchCard
                                key={match.id}
                                match={match}
                                teams={activeTournament.teams}
                                onSetWinner={handleSetWinner}
                                isChampionship={roundNum === maxRound}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create Tournament Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>NEW TOURNAMENT</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-stone-300">Tournament Name</Label>
                <Input value={tName} onChange={e => setTName(e.target.value)} placeholder="Spring Classic 2026"
                  className="bg-stone-800 border-stone-600 text-white" data-testid="tournament-name-input" />
              </div>
              <div>
                <Label className="text-stone-300">Format</Label>
                <Select value={tFormat} onValueChange={v => setTFormat(v)}>
                  <SelectTrigger className="bg-stone-800 border-stone-600 text-white" data-testid="tournament-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_elimination">Single Elimination</SelectItem>
                    <SelectItem value="pools_then_bracket">Pools + Championship Bracket</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-300">Number of Teams</Label>
                <Select value={String(tNumTeams)} onValueChange={v => { setTNumTeams(Number(v)); setTTeamNames(Array(Number(v)).fill('')); }}>
                  <SelectTrigger className="bg-stone-800 border-stone-600 text-white" data-testid="num-teams-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[4, 6, 8, 10, 12, 16].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} Teams</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {tFormat === 'pools_then_bracket' && (
                <div>
                  <Label className="text-stone-300">Number of Pools</Label>
                  <Select value={String(tPools.length)} onValueChange={v => {
                    const poolNames = Array.from({ length: Number(v) }, (_, i) => `Pool ${String.fromCharCode(65 + i)}`);
                    setTPools(poolNames);
                  }}>
                    <SelectTrigger className="bg-stone-800 border-stone-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2, 3, 4].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} Pools</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-2">
                    {tPools.map((p, i) => (
                      <span key={i} className="text-xs bg-blue-900/40 text-blue-400 px-2 py-1 rounded">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-stone-300">Team Names</Label>
                <p className="text-stone-500 text-xs mb-2">Enter each team name. They'll be seeded in this order.</p>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {tTeamNames.map((name, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-stone-500 text-xs w-6 text-right">{i + 1}.</span>
                      <Input value={name} onChange={e => {
                        const arr = [...tTeamNames]; arr[i] = e.target.value; setTTeamNames(arr);
                      }} placeholder={`Team ${i + 1}`}
                        className="bg-stone-800 border-stone-600 text-white text-sm"
                        data-testid={`team-name-input-${i}`} />
                      {tFormat === 'pools_then_bracket' && (
                        <span className="text-xs text-blue-400 shrink-0">{tPools[i % tPools.length]}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full bg-amber-600 hover:bg-amber-500 text-white" data-testid="submit-tournament-btn">
                <Trophy size={16} className="mr-2" /> Create Tournament
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
